import QRCode from 'qrcode'

/**
 * QR encoding for the `otpauth://` enrollment URI.
 *
 * ┌─ ⛔ THIS WAS 258 LINES OF HAND-ROLLED REED-SOLOMON AND IT WAS BROKEN ──────┐
 * │ Shipped in FOUNDATION, reviewed, and produced a QR NO DECODER COULD READ,  │
 * │ from its first commit until 2026-07-30. Found the first time anyone tried   │
 * │ to scan it, which was the first time anyone could: 2FA enrolment needs a    │
 * │ session, and no account existed until that day.                            │
 * │                                                                            │
 * │ PROVEN WITH A CONTROL rather than assumed. The same render-and-decode       │
 * │ harness, on the same URI:                                                  │
 * │                                                                            │
 * │   reference encoder -> 41 modules (v6) -> DECODES, exact match             │
 * │   the old encodeQr  -> 41 modules (v6) -> FAILS TO DECODE                  │
 * │                                                                            │
 * │ Finder patterns and both timing patterns matched the reference exactly, so  │
 * │ the layout was right and the data, error correction or masking was not.     │
 * │ The exact fault was never root-caused, and that is recorded rather than     │
 * │ glossed: an attempt to decode the format-info bits reported the REFERENCE   │
 * │ QR as invalid too, which is impossible, so the probe was wrong and the      │
 * │ investigation stopped there rather than reporting a cause it had not        │
 * │ established.                                                               │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ ⛔ AND THE HEADER COMMENT DREW THE BOUNDARY IN THE WRONG PLACE ────────────┐
 * │ The original said, of a hand-written QR encoder:                           │
 * │                                                                            │
 * │   "Deliberately NOT general purpose: it supports byte mode and versions up  │
 * │    to 10 ... If a longer payload is ever needed, USE A LIBRARY RATHER THAN  │
 * │    EXTENDING THIS."                                                        │
 * │                                                                            │
 * │ The author saw a boundary and drew it around PAYLOAD LENGTH. The boundary   │
 * │ was writing a Reed-Solomon encoder at all.                                 │
 * │                                                                            │
 * │ A NEW VARIANT OF THE RATIONALISING COMMENT: it does not talk a reader out   │
 * │ of checking, it talks them out of checking THE RIGHT THING, by naming a     │
 * │ plausible adjacent risk. A reader who took it seriously would have          │
 * │ watched the payload length, which never grew, and never questioned the      │
 * │ 200 lines of finite-field arithmetic underneath. Recorded in ops/README.    │
 * │                                                                            │
 * │ The supply-chain argument for hand-rolling was reasonable in the abstract   │
 * │ and cost a broken security-enrolment path that shipped undetected. A        │
 * │ dependency thousands of people scan daily is the stronger side of that      │
 * │ trade.                                                                     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ THE SWAP IS NOT THE FIX. `tests/qr-encoding.test.ts` DECODES what this
 * produces, with an independent decoder, and asserts the URI round-trips
 * exactly. Without that, this is a different unverified implementation of the
 * same never-exercised path.
 *
 * The manual-entry key is still always shown alongside the QR, so a failure here
 * degrades to "type the key in" rather than blocking enrollment. That fallback is
 * what kept this survivable when it was broken.
 */

/**
 * A square grid of dark/light modules.
 *
 * ⚠️ INTERFACE UNCHANGED ON PURPOSE. `TwoFactorSetup.tsx` renders from this shape
 * and was not touched by the replacement, so the rendering path that has always
 * worked is not also being changed at the same time as the encoder that did not.
 */
export type QrMatrix = { size: number; modules: boolean[][] }

/**
 * Encode a string as a QR matrix.
 *
 * Returns null rather than throwing if the payload cannot be encoded: the caller
 * falls back to showing the manual key alone, which is a degraded but working
 * enrollment path.
 *
 * Error correction level M, matching what the old implementation targeted.
 *
 * ⚠️ PINNED BY A SEPARATE TEST, because the round-trip CANNOT see it. Level L
 * produces a valid QR of the same URI, so every decode assertion passes while
 * damage tolerance drops from ~15% to ~7% and scans degrade in poor light.
 * Found by mutation; this sentence previously claimed the round-trip asserted
 * it, which was false.
 */
export function encodeQr(text: string): QrMatrix | null {
  try {
    const qr = QRCode.create(text, { errorCorrectionLevel: 'M' })
    const size = qr.modules.size
    const modules: boolean[][] = []
    for (let y = 0; y < size; y++) {
      const row: boolean[] = []
      for (let x = 0; x < size; x++) row.push(Boolean(qr.modules.get(x, y)))
      modules.push(row)
    }
    return { size, modules }
  } catch {
    // A payload too large for any version, or an encoder error. The manual key
    // is the working path from here.
    return null
  }
}
